import { Tooltip, getTestAttributes, slug } from '@chillibean/component-library';
import {
  OptionsModal,
  DatePicker,
  FilterAccordion,
  FilterDate,
  FilterSelect,
  FilterInput,
  ToggleCheckbox,
  icons,
  ToggleSwitch,
  Button,
  enforceUTC,
} from '@chillibean/component-library/legacy';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useState } from 'react';

import {
  ChildWrapper,
  FilterElement,
  FilterInfoWrapper,
  InfoText,
  RemoveFilter,
  ToggleCheckboxWrapper,
  getDateFilterText,
  getFiltersNumberText,
  parseDate,
} from './AssetsFilterUtils';

function AssetsFilter(props) {
  const { showModal, setShowFilterModal, searchableAttributes, handleToggle, handleApply, setFiltersNumber } = props;
  const [appliedFilterCriteria, setAppliedFilterCriteria] = useState([]);
  const [matchExactly, setMatchExactly] = useState(true);
  const [dateRange, setDateRange] = useState(null);
  const [appliedDateRangeFilter, setAppliedDateRangeFilter] = useState(false);
  const [criteriaOpen, setCriteriaOpen] = useState(true);
  const [dateOpen, setDateOpen] = useState(false);
  let criteriaPosition = 1;

  const getFilterOptions = () => {
    return searchableAttributes.map((attribute) => {
      return attribute.title;
    });
  };

  const setFilterValue = (value, id) => {
    const updatedFilters = [...appliedFilterCriteria];
    const selectedFilter = updatedFilters.find((filter) => filter.id === id);
    if (selectedFilter.type === 'date') {
      const [dateISO] = enforceUTC(value).toISOString().split('T');

      selectedFilter.value = dateISO;
    } else {
      selectedFilter.value = value;
    }
    setAppliedFilterCriteria(updatedFilters);
  };

  const getFilterInput = (filter) => {
    let input;
    switch (filter.type) {
      case 'single_line':
        input = (
          <FilterInput
            fieldID={`${criteriaPosition}`}
            onInput={(e) => setFilterValue(e, filter.id)}
            startValue={filter.value || ''}
          />
        );
        criteriaPosition += 1;
        break;
      case 'descriptive':
        input = (
          <FilterInput
            fieldID={`${criteriaPosition}`}
            onInput={(e) => setFilterValue(e, filter.id)}
            startValue={filter.value || ''}
          />
        );
        criteriaPosition += 1;
        break;
      case 'dropdown':
        input = (
          <FilterSelect
            fieldID={`${criteriaPosition}`}
            options={filter.options}
            onSelect={(e) => setFilterValue(e, filter.id)}
            startValue={filter.value}
            maxDropdownHeight={250}
          />
        );
        criteriaPosition += 1;
        break;
      case 'checkbox':
        input = (
          <ToggleCheckboxWrapper>
            <ToggleCheckbox
              fieldID={`${criteriaPosition}`}
              positiveLabel="yes"
              negativeLabel="no"
              onSelect={(e) => setFilterValue(e, filter.id)}
              startValue={filter.value}
              width="100%"
              height="48px"
            />
          </ToggleCheckboxWrapper>
        );
        criteriaPosition += 1;
        break;
      case 'date':
        input = (
          <FilterDate
            onSelect={(e) => setFilterValue(e, filter.id)}
            startValue={filter.value}
            defaultText="Choose date"
          />
        );
        criteriaPosition += 1;
        break;
      default:
        input = <FilterInput isDisabled />;
        break;
    }
    return input;
  };

  const setInitialFilterValue = (filter) => {
    const newFilter = filter;
    switch (filter.type) {
      case 'checkbox':
        newFilter.value = true;
        break;
      default:
        newFilter.value = null;
        break;
    }
    return newFilter;
  };

  const removeFilter = (id) => {
    const idxToDelete = appliedFilterCriteria.findIndex((filter) => filter.id === id);
    const newFilters = [...appliedFilterCriteria];
    newFilters.splice(idxToDelete, 1);
    setAppliedFilterCriteria(newFilters);
  };

  const removeAllFilters = (e) => {
    setAppliedFilterCriteria([]);
    e.stopPropagation();
  };

  const addFilter = () => {
    const filterId = _.uniqueId();
    setAppliedFilterCriteria([...appliedFilterCriteria, { id: filterId, value: null, isApplied: false }]);
  };

  const setFilterType = (newTitle, id) => {
    const updatedFilters = [...appliedFilterCriteria];
    const searchableFilter = searchableAttributes.find((filter) => filter.title === newTitle);
    const newFilter = updatedFilters.find((filter) => filter.id === id);
    let mergedFilter = { ...newFilter, ...searchableFilter };
    mergedFilter = setInitialFilterValue(mergedFilter);
    const oldIndex = updatedFilters.findIndex((filter) => {
      return filter.id === id;
    });
    updatedFilters.splice(oldIndex, 1, mergedFilter);
    setAppliedFilterCriteria(updatedFilters);
  };

  const filterParams = () => {
    const params = {
      search_fields: {},
      relative_upload_time_range: 'all_time',
      all_info_fields_must_match: matchExactly.toString(),
    };
    if (dateRange) {
      [params.upload_time_range_from, params.upload_time_range_to] = dateRange;
    }
    let nonEmptyFilters = appliedFilterCriteria;
    nonEmptyFilters = nonEmptyFilters.filter((f) => f.value !== null);
    nonEmptyFilters.forEach((filter, i) => {
      let modifiedValue = filter.value;
      if (filter.type === 'checkbox') {
        modifiedValue = filter.value === true ? '1' : '0';
      }
      params.search_fields[i] = [filter.position, modifiedValue];
    });
    return params;
  };

  const filterExactly = () => {
    setMatchExactly(!matchExactly);
  };

  const appliedFilters = () => {
    const nonEmptyFilters = appliedFilterCriteria.filter((f) => f.value !== null);
    return nonEmptyFilters;
  };

  const applyFilters = () => {
    handleToggle();
    const params = filterParams();
    handleApply(params);
    setFiltersNumber(appliedFilters().length + (appliedDateRangeFilter === true ? 1 : 0));
    setShowFilterModal(!showModal);
  };

  const setRange = (start, end) => {
    const startRange = new Date(start);
    const endRange = new Date(end);
    const startDateString = parseDate(startRange, true);
    const endDateString = end ? parseDate(endRange, false) : null;
    setDateRange([startDateString, endDateString]);
    if (end) {
      setAppliedDateRangeFilter(true);
    }
  };

  const removeDateRangeFilter = (e) => {
    setDateRange(null);
    setAppliedDateRangeFilter(false);
    e.stopPropagation();
  };

  const validateFilterInput = () => {
    if (appliedFilterCriteria.length === 0) return false;

    const noValue = () => {
      let noValueCheck = false;
      appliedFilterCriteria.forEach((filter) => {
        if (!filter.value && filter.value !== false) {
          noValueCheck = true;
        }
      });
      return noValueCheck;
    };

    return noValue();
  };

  const onCriteriaAccordionClick = () => {
    const newState = !criteriaOpen;
    setCriteriaOpen(newState);
    if (dateOpen && newState === true) setDateOpen(false);
  };

  const onDateAccordionClick = () => {
    const newState = !dateOpen;
    setDateOpen(newState);
    if (newState === true && criteriaOpen) setCriteriaOpen(false);
  };
  const handleCriteriaAccordion = () => {
    return criteriaOpen;
  };

  const handleDateAccordion = () => {
    return dateOpen;
  };

  return (
    <OptionsModal
      header="Filter options"
      onDismiss={handleToggle}
      onSubmit={applyFilters}
      showModal={showModal}
      testId="Options modal"
    >
      <FilterAccordion
        id={_.uniqueId()}
        testId="criteria-accordion"
        title={<div>Filter by criteria</div>}
        handleClick={() => onCriteriaAccordionClick()}
        manualOpen={handleCriteriaAccordion()}
        appliedFilter={
          appliedFilters().length > 0 ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              <div>{getFiltersNumberText(appliedFilters().length)}</div>
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => removeAllFilters(e)}
                onKeyDown={(e) => removeAllFilters(e)}
                style={{ alignSelf: 'center', display: 'flex' }}
              >
                {icons.roundExitSecondary(16, 16, '#29354B')}
              </div>
            </div>
          ) : (
            <div> All assets</div>
          )
        }
      >
        <FilterInfoWrapper>
          <InfoText>MATCH EXACTLY</InfoText>
          <Tooltip
            content="When this option is turned on, the results will exactly match all of the filters you have applied. When turned off the results will match at least one or more of the filters applied"
            id="match-exactly-toggle"
          >
            <div style={{ alignSelf: 'center', display: 'flex' }}>
              {icons.questionMark(21, 21, '#FFFFFF', '#29354B')}
            </div>
          </Tooltip>
          <div {...getTestAttributes(`match-exactly-toggle`)} style={{ zIndex: 999 }}>
            <ToggleSwitch onSelect={filterExactly} disabled={false} startValue={matchExactly} />
          </div>
        </FilterInfoWrapper>
        {appliedFilterCriteria.map((filter) => {
          return (
            <FilterElement key={filter.id}>
              <ChildWrapper>
                <FilterSelect
                  fieldID={`criteria-${criteriaPosition}`}
                  defaultText={filter.title || 'Choose filter type'}
                  options={getFilterOptions()}
                  onSelect={(e) => setFilterType(e, filter.id)}
                  startValue={filter.title}
                  maxDropdownHeight={250}
                />
              </ChildWrapper>
              <ChildWrapper>{getFilterInput(filter)}</ChildWrapper>
              <RemoveFilter
                {...getTestAttributes(`remove-filter-${slug(`${filter.title}`)}`)}
                style={{ cursor: 'pointer' }}
                onClick={() => removeFilter(filter.id)}
              >
                {icons.roundExitSecondary(16, 16, '#29354B')}
              </RemoveFilter>
            </FilterElement>
          );
        })}
        <Button
          name="Add Filter"
          buttonType="primary"
          actionType="submit"
          handleClick={addFilter}
          disable={validateFilterInput()}
          height={40}
        />
      </FilterAccordion>
      <div style={{ marginTop: 16, paddingBottom: 16 }}>
        <FilterAccordion
          testId="date-accordion"
          title={<>Filter by upload date</>}
          handleClick={() => onDateAccordionClick()}
          manualOpen={handleDateAccordion()}
          appliedFilter={
            appliedDateRangeFilter === true ? (
              <div style={{ display: 'flex', gap: '8px' }}>
                <div {...getTestAttributes('applied-range-indicator')}>
                  {`${getDateFilterText(dateRange[0])} - ${getDateFilterText(dateRange[1])}`}
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => removeDateRangeFilter(e)}
                  onKeyDown={(e) => removeDateRangeFilter(e)}
                  style={{ alignSelf: 'center', display: 'flex' }}
                >
                  {icons.roundExitSecondary(16, 16, '#29354B')}
                </div>
              </div>
            ) : (
              <div {...getTestAttributes('applied-range-indicator')} style={{ display: 'flex', gap: '8px' }}>
                All time
              </div>
            )
          }
        >
          <DatePicker
            onSelect={setRange}
            maxDate
            initialStartDate={dateRange ? dateRange[0] : null}
            initialEndDate={dateRange ? dateRange[1] : null}
          />
        </FilterAccordion>
      </div>
    </OptionsModal>
  );
}

AssetsFilter.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setShowFilterModal: PropTypes.func.isRequired,
  searchableAttributes: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]).isRequired,
  handleToggle: PropTypes.func.isRequired,
  handleApply: PropTypes.func.isRequired,
  setFiltersNumber: PropTypes.func.isRequired,
};

export default AssetsFilter;
